/**
 * Item Classification Store
 *
 * Zustand, DB-first. No localStorage.
 * Per-item cache keyed by itemId.
 *
 * Actions:
 *   fetchForItem(itemId)           — load active proposal + history
 *   runAtcAndPropose(...)          — run a classify agent on the item (classifyRunner), then cache its proposal
 *   accept(proposalId, userId)     — accept an agent proposal (RPC)
 *   setDirect(input)               — set classification directly as human (RPC)
 *   reject(proposalId, itemId)     — dismiss an agent proposal
 *   invalidate(itemId)             — clear local cache for an item (triggers refetch)
 */

import { create } from 'zustand';
import type { ItemClassificationProposal, ClassificationStatus } from '../../types/prioritization/itemClassification';
import {
  getActiveProposal,
  getProposalHistory,
  acceptProposal,
  partialAcceptProposal,
  setDirectClassification,
  rejectProposal,
  type SetDirectClassificationInput,
} from '../../lib/itemClassificationService';
import { classifyItem, type ClassifyAgent } from '../../lib/classifyRunner';

// ── Per-item state ────────────────────────────────────────────────────────────

interface ItemClassificationState {
  activeProposal: ItemClassificationProposal | null;
  history: ItemClassificationProposal[];
  /** Whether a fetch is in progress */
  loading: boolean;
  /** Most recent error */
  error: string | null;
  /** Whether an ATC run / mutation is in progress */
  mutating: boolean;
}

function emptyItemState(): ItemClassificationState {
  return {
    activeProposal: null,
    history: [],
    loading: false,
    error: null,
    mutating: false,
  };
}

// ── Store state + actions ─────────────────────────────────────────────────────

interface ItemClassificationStoreState {
  /** Cache keyed by itemId */
  byItem: Record<string, ItemClassificationState>;

  /** Load the active proposal + full history for an item. */
  fetchForItem: (itemId: string) => Promise<void>;

  /**
   * Run a classify agent (the Air Traffic Controller unless another is named)
   * for an item; its proposal lands through propose_item_classification and
   * the item's classification_status becomes 'proposed'.
   */
  runAtcAndPropose: (params: {
    itemId: string;
    workspaceId: string;
    title: string;
    description: string;
    agent?: ClassifyAgent;
    runByUserId?: string | null;
  }) => Promise<{ success: boolean; error?: string }>;

  /** Accept the active proposal for an item. */
  accept: (params: {
    proposalId: string;
    itemId: string;
    userId: string;
  }) => Promise<{ success: boolean; error?: string }>;

  /**
   * Partially accept an active proposal — apply just category, just altitude, or both.
   * Passing neither acceptCategory nor acceptAltitude is equivalent to rejection.
   */
  partialAccept: (params: {
    proposalId: string;
    itemId: string;
    userId: string;
    acceptCategory: boolean;
    acceptAltitude: boolean;
  }) => Promise<{ success: boolean; error?: string }>;

  /** Directly set the classification (human entry — bypasses proposed state). */
  setDirect: (
    input: SetDirectClassificationInput,
  ) => Promise<{ success: boolean; error?: string }>;

  /** Dismiss the active proposal without setting a new classification. */
  reject: (params: {
    proposalId: string;
    itemId: string;
  }) => Promise<{ success: boolean; error?: string }>;

  /** Clear the cache for a specific item (forces a fresh fetch). */
  invalidate: (itemId: string) => void;

  /** Convenience selector: get the current classification_status for an item. */
  getClassificationStatus: (itemId: string) => ClassificationStatus;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useItemClassificationStore = create<ItemClassificationStoreState>(
  (set, get) => ({
    byItem: {},

    // ── fetchForItem ────────────────────────────────────────────────────────

    fetchForItem: async (itemId) => {
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), loading: true, error: null },
        },
      }));

      const [proposalResult, historyResult] = await Promise.all([
        getActiveProposal(itemId),
        getProposalHistory(itemId),
      ]);

      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: {
            ...(s.byItem[itemId] ?? emptyItemState()),
            loading: false,
            activeProposal: proposalResult.data,
            history: historyResult.data,
            error: proposalResult.error ?? historyResult.error,
          },
        },
      }));
    },

    // ── runAtcAndPropose ────────────────────────────────────────────────────

    runAtcAndPropose: async ({ itemId, workspaceId, title, description, agent, runByUserId }) => {
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: true, error: null },
        },
      }));

      try {
        const outcome = await classifyItem({ agent, workspaceId, itemId, title, description, runByUserId });

        if (outcome.outcome !== 'proposed') {
          set((s) => ({
            byItem: {
              ...s.byItem,
              [itemId]: {
                ...(s.byItem[itemId] ?? emptyItemState()),
                mutating: false,
                error: outcome.reason,
              },
            },
          }));
          return { success: false, error: outcome.reason };
        }
        const proposalResult = { data: outcome.proposal };

        // Update local cache
        set((s) => {
          const existing = s.byItem[itemId] ?? emptyItemState();
          const newProposal = proposalResult.data;
          return {
            byItem: {
              ...s.byItem,
              [itemId]: {
                ...existing,
                mutating: false,
                activeProposal: newProposal,
                history: newProposal ? [newProposal, ...existing.history] : existing.history,
                error: null,
              },
            },
          };
        });

        return { success: true };
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Unknown error';
        set((s) => ({
          byItem: {
            ...s.byItem,
            [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: false, error: errMsg },
          },
        }));
        return { success: false, error: errMsg };
      }
    },

    // ── accept ──────────────────────────────────────────────────────────────

    accept: async ({ proposalId, itemId, userId }) => {
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: true, error: null },
        },
      }));

      const result = await acceptProposal(proposalId, userId);

      if (result.success) {
        // Optimistic update: clear active proposal and move it to history as accepted
        // so the UI responds immediately without waiting for a round-trip.
        set((s) => {
          const existing = s.byItem[itemId] ?? emptyItemState();
          const nowAccepted = existing.activeProposal
            ? { ...existing.activeProposal, status: 'accepted' as const }
            : null;
          return {
            byItem: {
              ...s.byItem,
              [itemId]: {
                ...existing,
                mutating: false,
                activeProposal: null,
                history: nowAccepted
                  ? [nowAccepted, ...existing.history.filter((p) => p.id !== proposalId)]
                  : existing.history,
                error: null,
              },
            },
          };
        });
        // Background DB sync to get authoritative state
        void get().fetchForItem(itemId);
      } else {
        set((s) => ({
          byItem: {
            ...s.byItem,
            [itemId]: {
              ...(s.byItem[itemId] ?? emptyItemState()),
              mutating: false,
              error: result.error ?? null,
            },
          },
        }));
      }

      return result;
    },

    // ── partialAccept ───────────────────────────────────────────────────────

    partialAccept: async ({ proposalId, itemId, userId, acceptCategory, acceptAltitude }) => {
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: true, error: null },
        },
      }));

      const result = await partialAcceptProposal(proposalId, userId, acceptCategory, acceptAltitude);

      if (result.success) {
        const accepted = acceptCategory || acceptAltitude;
        set((s) => {
          const existing = s.byItem[itemId] ?? emptyItemState();
          const updatedProposal = existing.activeProposal
            ? {
                ...existing.activeProposal,
                status: accepted ? ('accepted' as const) : ('rejected' as const),
              }
            : null;
          return {
            byItem: {
              ...s.byItem,
              [itemId]: {
                ...existing,
                mutating: false,
                activeProposal: null,
                history: updatedProposal
                  ? [updatedProposal, ...existing.history.filter((p) => p.id !== proposalId)]
                  : existing.history,
                error: null,
              },
            },
          };
        });
        void get().fetchForItem(itemId);
      } else {
        set((s) => ({
          byItem: {
            ...s.byItem,
            [itemId]: {
              ...(s.byItem[itemId] ?? emptyItemState()),
              mutating: false,
              error: result.error ?? null,
            },
          },
        }));
      }

      return result;
    },

    // ── setDirect ───────────────────────────────────────────────────────────

    setDirect: async (input) => {
      const { itemId } = input;
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: true, error: null },
        },
      }));

      const result = await setDirectClassification(input);

      if (result.success) {
        await get().fetchForItem(itemId);
      }

      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: {
            ...(s.byItem[itemId] ?? emptyItemState()),
            mutating: false,
            error: result.error ?? null,
          },
        },
      }));

      return result;
    },

    // ── reject ──────────────────────────────────────────────────────────────

    reject: async ({ proposalId, itemId }) => {
      set((s) => ({
        byItem: {
          ...s.byItem,
          [itemId]: { ...(s.byItem[itemId] ?? emptyItemState()), mutating: true, error: null },
        },
      }));

      const result = await rejectProposal(proposalId);

      if (result.success) {
        set((s) => {
          const existing = s.byItem[itemId] ?? emptyItemState();
          return {
            byItem: {
              ...s.byItem,
              [itemId]: {
                ...existing,
                mutating: false,
                activeProposal: null,
                history: existing.history.map((p) =>
                  p.id === proposalId ? { ...p, status: 'rejected' as const } : p,
                ),
                error: null,
              },
            },
          };
        });
      } else {
        set((s) => ({
          byItem: {
            ...s.byItem,
            [itemId]: {
              ...(s.byItem[itemId] ?? emptyItemState()),
              mutating: false,
              error: result.error ?? null,
            },
          },
        }));
      }

      return result;
    },

    // ── invalidate ──────────────────────────────────────────────────────────

    invalidate: (itemId) => {
      set((s) => {
        const next = { ...s.byItem };
        delete next[itemId];
        return { byItem: next };
      });
    },

    // ── getClassificationStatus ─────────────────────────────────────────────

    getClassificationStatus: (itemId) => {
      const s = get().byItem[itemId];
      if (!s) return 'empty';
      if (s.activeProposal?.status === 'active') return 'proposed';
      const hasAccepted = s.history.some((p) => p.status === 'accepted');
      if (hasAccepted) return 'approved';
      return 'empty';
    },
  }),
);
